<template>
  <div :class="$style['hypo-fee-roaloi']">
    <!-- Add Button -->
    <div v-if="showRoaLoi && roaloiDataValid && !contentOpened" @click="contentOpened = true">
      <mds-button variation="flat" icon="plus" type="button" size="small">
        {{ translate('addROALOI') }}
      </mds-button>
    </div>
    <mds-layout-grid v-if="contentOpened" :class="$style['non-changing-content']">
      <mds-row :class="$style['title']">
        <mds-col :class="$style['title-text']"> {{ translate('accumulationOptions') }} </mds-col>
        <mds-col :class="$style['title-button']">
          <mds-button
            variation="flat"
            icon="remove"
            type="button"
            size="small"
            @click="handleRemoveButtonClick"
          >
            {{ translate('remove') }}
          </mds-button>
        </mds-col>
      </mds-row>
      <mds-row>
        <mds-col :cols="12" :class="$style['switch-buttons']">
          <mds-button-group
            :content="switchButtonContent"
            full-width
            size="small"
            aria-label="Switch ROA or LOI"
            @mds-button-group-item-active="setActiveItem"
          ></mds-button-group>
        </mds-col>
        <mds-col :cols="12" :class="$style['explanation']">
          {{ activeTab === 'roa' ? translate('ROAExplanation') : translate('LOIExplanation') }}
        </mds-col>
        <mds-col :cols="12">
          <mds-switch v-model="activeTabData.diffAmountSelected" value="same-amount" size="small">
            {{
              activeTabData.diffAmountSelected
                ? translate('differentAmounts')
                : translate('sameAmount')
            }}
          </mds-switch>
        </mds-col>
      </mds-row>
    </mds-layout-grid>
    <!-- Switchable Content -->
    <mds-layout-grid v-if="contentOpened">
      <!-- Same Amount -->
      <mds-row v-if="!activeTabData.diffAmountSelected" :class="$style['same-amount-content']">
        <mds-col :cols="12" :class="$style['title-text']">
          <p>{{ translate('rightsOfAccumulation') }} {{ currencySymbol }}</p>
        </mds-col>
        <mds-col :cols="12">
          <p>{{ translate('applyOne') }}</p>
        </mds-col>
        <mds-col :cols="12">
          <mds-input
            size="small"
            hidden-label
            label="Amount"
            :value="formatter.number(activeTabData.sameAmount, 'integer')"
            @change="handleSameAmountChange($event)"
          >
          </mds-input>
        </mds-col>
        <mds-col :cols="12">
          <p>{{ translate('fundFamiliesInThisPortfolio') }}</p>
        </mds-col>
        <mds-col :cols="12">
          <mds-tag-container>
            <mds-tag v-for="item in activeTabData.fundFamilies" :key="item.fundFamilyId">
              {{ item.fundFamilyName }}
            </mds-tag>
          </mds-tag-container>
        </mds-col>
      </mds-row>
      <!-- Different Amount  -->
      <mds-row v-if="activeTabData.diffAmountSelected" :class="$style['diff-amount-content']">
        <mds-col :col="12">
          <mds-table>
            <mds-thead>
              <mds-th> {{ translate('fundFamily') }} </mds-th>
              <mds-th right-aligned style="width: 120px;">
                {{ translate('amount') }} {{ currencySymbol }}
              </mds-th>
            </mds-thead>
            <mds-tbody>
              <mds-tr v-for="item in activeTabData.fundFamilies" :key="item.fundFamilyId">
                <mds-td>
                  <p :class="$style['fund-family-name']">{{ item.fundFamilyName }}</p>
                </mds-td>
                <mds-td right-aligned>
                  <mds-input
                    size="small"
                    hidden-label
                    label="Amount"
                    :value="formatter.number(item.fundFamilyAmount, 'integer')"
                    @change="handleFundFamilyAmountChange($event, item.fundFamilyId)"
                  >
                  </mds-input
                ></mds-td>
              </mds-tr>
            </mds-tbody> </mds-table
        ></mds-col>
      </mds-row>
    </mds-layout-grid>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import MdsButtonGroup from '@mds/button-group';
import MdsSwitch from '@mds/switch';
import MdsInput from '@mds/input';
import { MdsTag, MdsTagContainer } from '@mds/tag';
import { MdsButton } from '@mds/button';
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import cloneDeep from 'lodash.clonedeep';
import { parseNumber, isInvalidAmount } from '../../utils/utils';

const defaultData = {
  switchButtonContent: [
    { id: '1', labelKey: 'rightOfAcc', active: true },
    { id: '2', labelKey: 'letterOfIntent', active: false },
  ],
  contentOpened: false,
  activeTab: 'roa',
  roa: {
    diffAmountSelected: true,
    sameAmount: 0, // used when same amount is selected
    fundFamilies: [],
  },
  loi: {
    diffAmountSelected: true,
    sameAmount: 0, // used when same amount is selected
    fundFamilies: [],
  },
};

export default {
  name: 'roa-loi',
  components: {
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
    MdsButtonGroup,
    MdsSwitch,
    MdsInput,
    MdsTag,
    MdsTagContainer,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsButton,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    roaloiData: {
      type: Object,
      default() {
        return {};
      },
    },
    currencySymbol: {
      type: String,
      default: '$',
    },
    showRoaLoi: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      ...cloneDeep(defaultData),
    };
  },
  computed: {
    roaloiDataValid() {
      return (
        this.roaloiData &&
        this.roaloiData.roa &&
        this.roaloiData.roa.fundFamilies &&
        this.roaloiData.roa.fundFamilies.length > 0 &&
        this.roaloiData.loi &&
        this.roaloiData.loi.fundFamilies &&
        this.roaloiData.loi.fundFamilies.length > 0
      );
    },
    activeTabData() {
      return this[this.activeTab];
    },
  },
  watch: {
    // for stroybook testing purpose
    roaloiData: {
      handler() {
        this.setData();
      },
    },
    deep: true,
  },
  created() {
    this.setData();
  },
  methods: {
    setData() {
      this.switchButtonContent = this.switchButtonContent.map(i => ({
        ...i,
        text: this.translate(i.labelKey),
      }));
      if (this.roaloiDataValid) {
        ['roa', 'loi'].forEach(key => {
          this[key] = { ...cloneDeep(defaultData[key]), ...cloneDeep(this.roaloiData[key]) };
          // When Same Amount, use first element data as sample
          if (!this[key].diffAmountSelected) {
            this[key].sameAmount = this[key].fundFamilies[0].fundFamilyAmount;
          }
        });
      }
    },
    handleRemoveButtonClick() {
      this.contentOpened = false;
      Object.keys(defaultData).forEach(key => {
        this.$data[key] = cloneDeep(defaultData[key]);
      });
      this.setData();
      this.$emit('roa-loi-removed');
    },
    setActiveItem(event) {
      this.activeTab = Number(event.currentTarget.id) === 1 ? 'roa' : 'loi';
      this.switchButtonContent.forEach(item => {
        if (item.id === event.currentTarget.id) {
          item.active = true;
        } else {
          item.active = false;
        }
      });
    },
    handleSameAmountChange(e) {
      const amount = parseNumber(e.target.value);
      if (isInvalidAmount(amount)) {
        e.target.value = this.formatter.number(this.activeTabData.sameAmount, 'integer');
      } else {
        e.target.value = this.formatter.number(amount, 'integer');
        this.activeTabData.sameAmount = amount;
        this.emitData();
      }
    },
    handleFundFamilyAmountChange(e, fundFamilyId) {
      const amount = parseNumber(e.target.value);
      const targetFamily =
        this.activeTabData.fundFamilies.filter(family => family.fundFamilyId === fundFamilyId)[0] ||
        null;
      if (!targetFamily) {
        return;
      }
      if (isInvalidAmount(amount)) {
        e.target.value = this.formatter.number(targetFamily.fundFamilyAmount, 'integer');
      } else {
        e.target.value = this.formatter.number(amount, 'integer');
        targetFamily.fundFamilyAmount = amount;
        this.emitData();
      }
    },
    emitData() {
      const { roa, loi } = this.$data;
      const clonedData = cloneDeep({ roa, loi });
      // When emitData, apply same amount values if diffAmount is not selected
      Object.keys(clonedData).forEach(key => {
        const dataSet = clonedData[key];
        if (!dataSet.diffAmountSelected) {
          dataSet.fundFamilies.forEach(family => {
            family.fundFamilyAmount = dataSet.sameAmount;
          });
        }
        delete dataSet.sameAmount;
      });
      this.$emit('amount-changed', clonedData);
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/typography';

.hypo-fee-roaloi {
  @include mds-body-text-s();
  margin-bottom: $mds-space-1-and-a-half-x;
  margin-top: -$mds-space-half-x;
  max-width: 360px;
  p {
    margin: 0;
  }

  .title {
    margin-bottom: $mds-space-2-and-a-half-x;
    &-text {
      margin-top: 3px;
      @include mds-body-text-s($bold: true);
    }
    &-button {
      display: flex;
      justify-content: flex-end;
    }
  }

  .non-changing-content {
    .switch-buttons {
      margin-top: -$mds-space-1-x;
    }
    .explanation {
      margin: $mds-space-2-x 0;
    }
  }

  .same-amount-content {
    margin-top: $mds-space-2-x;
    .mds-layout-grid__col--12 {
      margin-top: $mds-space-half-x; // 4px
    }
    .mds-input {
      margin-bottom: $mds-space-1-and-a-half-x; // 12px
    }
    .mds-tag-container {
      max-height: 90px;
      overflow-y: auto;
    }
  }

  .diff-amount-content {
    margin-top: $mds-space-2-x;
    .mds-layout-grid__col {
      max-height: 360px;
      overflow-y: auto;
    }

    .fund-family-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .mds-input {
      max-width: 100px;
      margin-left: auto;
      margin-right: $mds-space-half-x;
    }
  }
}
</style>
